import React, { useMemo, useState, useEffect } from 'react';
import { PLATS, COST_FIELDS, NM, PRE_MONTHS } from '../utils/constants';
import { calcPlatform, getGameState, calcIRR, getGameCashflows } from '../utils/calc';
import { fu, ml, mlSafe } from '../utils/format';
import { getCostState, getDealTerms } from '../utils/stateFactory';

const STATE_ORDER = { 'Pre-Launch': 0, 'Pre-Recoup': 1, 'Recouped': 2, 'Unknown': 3 };
const STATE_STYLE = {
  'Pre-Launch': { color: 'var(--amber)', bg: 'rgba(245,166,35,.1)', border: 'rgba(245,166,35,.3)' },
  'Pre-Recoup': { color: 'var(--blue)',  bg: 'var(--blue-dim)',     border: 'rgba(77,159,255,.3)' },
  'Recouped':   { color: 'var(--green)', bg: 'var(--green-dim)',    border: 'rgba(0,217,139,.3)'  },
  'Unknown':    { color: 'var(--t3)',    bg: 'var(--s2)',           border: 'var(--e1)'           },
};
const RAG_STYLE = {
  'Red':   { color: '#ff4444', bg: 'rgba(255,68,68,.12)',   border: 'rgba(255,68,68,.3)'   },
  'Amber': { color: '#f5a623', bg: 'rgba(245,166,35,.12)',  border: 'rgba(245,166,35,.3)'  },
  'Green': { color: '#00d98b', bg: 'rgba(0,217,139,.12)',   border: 'rgba(0,217,139,.3)'   },
  '':      { color: 'var(--t4)', bg: 'transparent',         border: 'var(--e1)'            },
};

function StateBadge({ state }) {
  const s = STATE_STYLE[state] || STATE_STYLE.Unknown;
  return (
    <span style={{ display:'inline-block', fontSize:10, fontWeight:600, letterSpacing:'.06em',
      textTransform:'uppercase', padding:'2px 9px', borderRadius:'var(--r)',
      color:s.color, background:s.bg, border:`1px solid ${s.border}` }}>
      {state}
    </span>
  );
}

function RAGSelect({ value, onChange }) {
  const s = RAG_STYLE[value] || RAG_STYLE[''];
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ fontSize:11, fontWeight:600, padding:'3px 24px 3px 9px',
        color: s.color, background: s.bg, border:`1px solid ${s.border}`,
        borderRadius:'var(--r)', cursor:'pointer', appearance:'none',
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5'%3E%3Cpath d='M0 0l4 5 4-5z' fill='%234a5570'/%3E%3C/svg%3E\")",
        backgroundRepeat:'no-repeat', backgroundPosition:'right 7px center' }}>
      <option value="">— RAG —</option>
      <option value="Red">Red</option>
      <option value="Amber">Amber</option>
      <option value="Green">Green</option>
    </select>
  );
}

export default function PortfolioPanel({ games, PS, CS, DT, currentMonthIdx, functionalCurrency, convertTotal, convert, startDate, ragStatus: ragProp, onUpdateRagStatus }) {
  const effectiveMonthIdx = currentMonthIdx ?? NM - 1;
  const hasCurrentMonth   = currentMonthIdx !== null && currentMonthIdx !== undefined;

  // RAG status stored in Supabase app_settings per game
  // Use prop directly — saved in App.js state and Supabase
  const ragStatus = ragProp || {};

  function updateRAG(game, val) {
    onUpdateRagStatus?.(game, val);
  }

  const fc = functionalCurrency || 'USD';

  const rows = useMemo(() => {
    return games.map(g => {
      const state  = getGameState(PS, CS, DT, g, PLATS, currentMonthIdx);
      const dt     = getDealTerms(DT, g);
      const costs  = getCostState(CS, g);

      // Original Budget — from deal terms, dev advance converted FC, pub/mkt already FC
      const devAdvRaw  = parseFloat(dt.devAdvanceAmt)  || 0;
      const budgetPub  = parseFloat(dt.pubCostsBudget) || 0;
      const budgetMkt  = parseFloat(dt.marketingBudget)|| 0;
      // Dev advance converted using current month rate
      const devAdvFC   = devAdvRaw > 0 && convert ? convert(devAdvRaw, fc, effectiveMonthIdx) : devAdvRaw;
      const origBudget = devAdvFC + budgetPub + budgetMkt;

      // Latest Budget (Current Forecast) — sum of all cost entries, already in FC, no conversion
      let latestBudget = 0;
      ['recoup','nonRecoup'].forEach(sec => {
        COST_FIELDS.forEach(({ key }) => {
          (costs[sec][key] || []).forEach(v => { latestBudget += parseFloat(v) || 0; });
        });
      });

      // Monthly cost arrays for FX conversion
      const monthlyCosts = Array(NM).fill(0);
      const monthlyRevenue = Array(NM).fill(0);

      ['recoup','nonRecoup'].forEach(sec => {
        COST_FIELDS.forEach(({ key }) => {
          (costs[sec][key] || []).forEach((v, arrayIdx) => {
            const mi = arrayIdx - PRE_MONTHS; // convert to month index
            // Include pre-forecast costs (negative mi) and up to effectiveMonthIdx
            if (mi <= effectiveMonthIdx) {
              const bucketIdx = Math.max(0, mi); // clamp to 0 for monthly array
              monthlyCosts[bucketIdx] += parseFloat(v) || 0;
            }
          });
        });
      });

      PLATS.forEach(p => {
        const st = PS[g]?.[p.key];
        if (!st?.enabled) return;
        calcPlatform(p.key, st).netRaw.forEach((v, i) => {
          if (i <= effectiveMonthIdx && v !== null) monthlyRevenue[i] += v;
        });
      });

      // Convert using FX rates per month
      const spendToDate   = convertTotal ? convertTotal(monthlyCosts.slice(0, effectiveMonthIdx + 1), fc) : monthlyCosts.reduce((a, v) => a + v, 0);
      const revenueToDate = convertTotal ? convertTotal(monthlyRevenue.slice(0, effectiveMonthIdx + 1), fc) : monthlyRevenue.reduce((a, v) => a + v, 0);

      let totalRecoupCosts = parseFloat(dt.devAdvanceAmt) || 0;
      COST_FIELDS.forEach(({ key }) => {
        (costs.recoup[key] || []).forEach(v => { totalRecoupCosts += parseFloat(v) || 0; });
      });
      // Includes pre-forecast costs
      const recoupPct = totalRecoupCosts > 0
        ? Math.round((revenueToDate / (convertTotal ? convertTotal([totalRecoupCosts], fc) : totalRecoupCosts)) * 100)
        : null;

      // IRR
      const cashflows = getGameCashflows(PS, CS, DT, g, PLATS);
      const firstIdx = cashflows.findIndex(v => v !== 0);
      const irrRaw = firstIdx >= 0 ? calcIRR(cashflows.slice(firstIdx)) : null;
      const irrAnnual = irrRaw !== null ? Math.pow(1 + irrRaw, 12) - 1 : null;

      // Both already in FC — no conversion needed
      const origBudgetFC   = origBudget;
      const latestBudgetFC = latestBudget;

      // Launch date — earliest of EA/FL across all platforms
      let launchIdx = null;
      PLATS.forEach(p => {
        const st = PS[g]?.[p.key];
        if (!st?.enabled) return;
        const ea = st.launchEA !== '' ? parseInt(st.launchEA) : null;
        const fl = st.launchFL !== '' ? parseInt(st.launchFL) : null;
        const earliest = ea !== null && fl !== null ? Math.min(ea, fl) : ea ?? fl;
        if (earliest !== null && (launchIdx === null || earliest < launchIdx)) launchIdx = earliest;
      });

      const isPreLaunch = state === 'Pre-Launch';

      return { name: g, state, origBudget: origBudgetFC, latestBudget: latestBudgetFC,
        spendToDate, revenueToDate: isPreLaunch ? null : revenueToDate,
        recoupPct: isPreLaunch ? null : recoupPct,
        irrAnnual, launchIdx };
    }).sort((a, b) => (STATE_ORDER[a.state] ?? 3) - (STATE_ORDER[b.state] ?? 3));
  }, [games, PS, CS, DT, currentMonthIdx, effectiveMonthIdx, fc, convertTotal]); // eslint-disable-line react-hooks/exhaustive-deps

  const ccyLabel = fc !== 'USD' ? ` (${fc})` : '';

  return (
    <div className="panel">
      {!hasCurrentMonth && (
        <div style={{ fontSize:11, color:'var(--amber)', background:'rgba(245,166,35,.08)',
          border:'1px solid rgba(245,166,35,.25)', borderRadius:'var(--r)',
          padding:'8px 14px', marginBottom:14 }}>
          ⚠ No month-end set — showing full forecast totals. Set a month-end date in the top bar to show actuals to date.
        </div>
      )}
      <div className="card">
        <div className="card-head">
          <span className="card-title">Portfolio Summary</span>
          <span className="card-meta">{games.length} game{games.length !== 1 ? 's' : ''}{fc !== 'USD' ? ` · ${fc}` : ''}</span>
        </div>
        <div className="card-body" style={{ padding: 0, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table className="portfolio-table">
            <thead>
              <tr>
                <th className="pt-lbl">Game</th>
                <th>State</th>
                <th>RAG</th>
                <th>Launch Date</th>
                <th>Original Budget{ccyLabel}</th>
                <th>Current Forecast{ccyLabel}</th>
                <th>{hasCurrentMonth ? `Spend to Date${ccyLabel}` : `Total Forecast Spend${ccyLabel}`}</th>
                <th>{hasCurrentMonth ? `Revenue to Date${ccyLabel}` : `Total Forecast Revenue${ccyLabel}`}</th>
                <th>Recoup %</th>
                <th>Forecast IRR (Annual)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.name} className="pt-row">
                  <td className="pt-name">{r.name}</td>
                  <td><StateBadge state={r.state} /></td>
                  <td><RAGSelect value={ragStatus[r.name] || ''} onChange={val => updateRAG(r.name, val)} /></td>
                  <td className="pt-num" style={{ fontSize: 11 }}>
                    {r.launchIdx !== null && r.launchIdx !== undefined ? mlSafe(r.launchIdx, startDate) : '—'}
                  </td>
                  <td className="pt-num">{r.origBudget    ? fu(r.origBudget)    : '—'}</td>
                  <td className="pt-num">{r.latestBudget  ? fu(r.latestBudget)  : '—'}</td>
                  <td className="pt-num">{r.spendToDate != null ? fu(r.spendToDate) : '—'}</td>
                  <td className="pt-num">{r.revenueToDate === null ? <span style={{color:'var(--t4)'}}>N/A</span> : r.revenueToDate ? fu(r.revenueToDate) : '—'}</td>
                  <td className="pt-num">
                    {r.recoupPct === null && r.state === 'Pre-Launch'
                      ? <span style={{color:'var(--t4)'}}>N/A</span>
                      : r.recoupPct !== null
                        ? <span style={{ color: r.recoupPct >= 100 ? 'var(--green)' : r.recoupPct > 50 ? 'var(--blue)' : 'var(--t1)' }}>
                            {r.recoupPct}%
                          </span>
                        : '—'}
                  </td>
                  <td className="pt-num">
                    {r.irrAnnual !== null
                      ? <span style={{ color: r.irrAnnual >= 0 ? 'var(--green)' : 'var(--red)' }}>
                          {Math.round(r.irrAnnual * 100)}%
                        </span>
                      : '—'}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={8} className="nomsg">No games configured.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
